/**
 * Договор ведения кассиров собственной кассы (`Resource.Receipts`, RP-OWN11) на границе клиента: ответ проверяется здесь, экран
 * получает только проверенные поля. `cashierId` — GUID узла структуры, он же `SourceCashierId` в Cashiers и `context.cashierId`
 * кассового агента; не меняется при переименовании и архивировании.
 */

export interface OwnCashier {
  cashierId: string
  name: string
  archived: boolean
  /** Версия узла: уходит в правку как `expectedRevision`. */
  revision: number
}

export interface OwnCashierRoster {
  structureRevision: number
  items: readonly OwnCashier[]
}

/** Настройки доступа кассира в Cashiers: нужна только версия (для отзыва) и признак «PIN задан». */
export interface OwnCashierAccess {
  version: number
  configured: boolean
}

function fail(what: string): never {
  throw new Error(`Ответ сервера не разобран: ${what}`)
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const record = (value: unknown, what: string): Record<string, unknown> => (isRecord(value) ? value : fail(what))
const text = (value: unknown, what: string): string => (typeof value === 'string' && value !== '' ? value : fail(what))
const flag = (value: unknown, what: string): boolean => (typeof value === 'boolean' ? value : fail(what))
const whole = (value: unknown, what: string): number => (typeof value === 'number' && Number.isSafeInteger(value) ? value : fail(what))

export function decodeCashier(raw: unknown, what = 'кассир'): OwnCashier {
  const item = record(raw, what)
  return {
    cashierId: text(item.cashierId, `${what}.cashierId`),
    name: text(item.name, `${what}.name`),
    archived: flag(item.archived, `${what}.archived`),
    revision: whole(item.revision, `${what}.revision`),
  }
}

export function decodeRoster(raw: unknown): OwnCashierRoster {
  const body = record(raw, 'список кассиров')
  if (!Array.isArray(body.items)) fail('список кассиров.items')
  return {
    structureRevision: whole(body.structureRevision, 'список кассиров.structureRevision'),
    items: body.items.map((item, index) => decodeCashier(item, `кассиры[${index}]`)),
  }
}

export function decodeAccess(raw: unknown): OwnCashierAccess {
  const body = record(raw, 'настройки доступа кассира')
  const version = whole(body.version, 'настройки доступа.version')
  if (version < 0) fail('настройки доступа.version')
  return { version, configured: flag(body.configured, 'настройки доступа.configured') }
}
